export default class ExchangeRatesTable {
    constructor(element) {
        this.selector = element;

        this.openDropdown();
        this.closeDropdown();
        this.searchDropdown();
        this.chooseDropdownOption();
    }

    openDropdown() {
        const dropdowns = this.selector.querySelectorAll('[data-dropdown]');

        dropdowns.forEach((dropdown) => {
            const selector = dropdown.querySelector('[data-dropdown-selector]');
            const list = dropdown.querySelector('[data-dropdown-list]');

            selector.addEventListener('click', () => {
                list.classList.remove('hidden');

                if (window.innerWidth < 640) {
                    document.body.classList.add('overflow-hidden');
                }
            });
        });
    }

    closeDropdown() {
        const dropdowns = this.selector.querySelectorAll('[data-dropdown]');
        const buttons = this.selector.querySelectorAll('[data-dropdown-close]');

        window.addEventListener('click', function(event) {
            dropdowns.forEach((dropdown) => {
                const list = dropdown.querySelector('[data-dropdown-list]');
                const selector = dropdown.querySelector('[data-dropdown-selector]');

                if (!selector.contains(event.target) && !list.contains(event.target)) {
                    list.classList.add('hidden');
                }
            });
        });

        buttons.forEach((button) => {
            button.addEventListener('click', () => {
                const list = button.closest('[data-dropdown-list]');

                list.classList.add('hidden');
                document.body.classList.remove('overflow-hidden');
            });
        });
    }

    searchDropdown() {
        const inputs = this.selector.querySelectorAll('[data-dropdown-search-input]');

        inputs.forEach((input) => {
            input.addEventListener('input', () => {
                const dropdown = input.closest('[data-dropdown]');
                const results = dropdown.querySelector('[data-dropdown-results]');
                const options = results.querySelectorAll('[data-dropdown-option]');
                const value = input.value.toLowerCase();

                options.forEach((option) => {
                    const text = option.querySelector('[data-dropdown-option-title]');

                    if (
                        option.dataset.dropdownOption.toLowerCase().includes(value) ||
                        text.innerText.toLowerCase().includes(value)
                    ) {
                        option.classList.remove('hidden');
                    } else {
                        option.classList.add('hidden');
                    }
                });
            });
        });
    }

    chooseDropdownOption() {
        const options = this.selector.querySelectorAll('[data-dropdown-option]');
        const results = this.selector.querySelector('[data-results]');
        const excludedSymbols = window.exchange_rates_table_data?.excluded_symbols || [];

        options.forEach((option) => {
            option.addEventListener('click', () => {
                const dropdown = option.closest('[data-dropdown]');
                const list = dropdown.querySelector('[data-dropdown-list]');
                const label = dropdown.querySelector('[data-dropdown-selector-label]');
                const baseCurrency = window.exchange_rates_table_data.data.find(obj => obj.symbol === option.dataset.dropdownOption);

                list.classList.add('hidden');
                label.innerHTML = baseCurrency.symbol;
                document.body.classList.remove('overflow-hidden');

                results.innerHTML = '';

                for (const targetCurrency of baseCurrency.rates) {
                    if (excludedSymbols.includes(targetCurrency.symbol)) {
                        continue;
                    }
                    results.insertAdjacentHTML('beforeend', this.formatTableRow(baseCurrency, targetCurrency));
                }

                options.forEach(opt => opt.classList.remove('option-active'));
                option.classList.add('option-active');
            });
        });
    }

    formatTableRow(baseCurrency, targetCurrency) {
        const market = targetCurrency.market;
        const change = market?.change_24h_pct;
        const volume = market?.volume_24h_eur;
        const priceInBase = this.getPriceInBaseCurrency(baseCurrency, targetCurrency);
        const formattedPriceInBase = this.formatDotNumberString(priceInBase);
        const priceWithCurrency = formattedPriceInBase === '-' ? '-' : `${formattedPriceInBase} ${baseCurrency.symbol}`;

        return `
            <tr>
                <td class="h-[60px]">
                    <a href="${window.exchange_rates_table_data.root_url}/${baseCurrency.symbol}/${targetCurrency.symbol}" class="flex flex-wrap items-center h-full">
                        <img class="w-[24px] h-[24px]" src="${window.exchange_rates_table_data.icon_directory_uri}/dist/images/currency-icons/${targetCurrency.symbol}.svg" alt="${targetCurrency.symbol}">
                        <span class="lg:block hidden text-grey-600 text-sm font-semibold ml-2">
                            ${targetCurrency.title}
                        </span>
                        <span class="text-grey-600 text-sm ml-1">
                            ${targetCurrency.symbol}
                        </span>
                    </a>
                </td>
                <td class="h-[60px]">
                    <a href="${window.exchange_rates_table_data.root_url}/${baseCurrency.symbol}/${targetCurrency.symbol}" class="flex flex-wrap items-center h-full px-4">
                        <span class="text-grey-600 font-semibold text-sm">
                            ${priceWithCurrency}
                        </span>
                    </a>
                </td>
                <td class="h-[60px]">
                    <a href="${window.exchange_rates_table_data.root_url}/${baseCurrency.symbol}/${targetCurrency.symbol}" class="flex flex-wrap items-center h-full px-4">
                        <span class="text-grey-600 font-semibold text-sm">
                            ${this.formatPct(change)}
                        </span>
                    </a>
                </td>
                <td class="h-[60px]">
                    <a href="${window.exchange_rates_table_data.root_url}/${baseCurrency.symbol}/${targetCurrency.symbol}" class="flex flex-wrap items-center h-full px-4">
                        <span class="text-grey-600 font-semibold text-sm">
                            ${this.formatEurVolume(volume)}
                        </span>
                    </a>
                </td>
            </tr>
        `;
    }

    formatPct(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return '-';
        }

        const n = Number(value);
        const sign = n > 0 ? '+' : '';
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n);
        return `${sign}${formatted}%`;
    }

    getPriceInBaseCurrency(baseCurrency, targetCurrency) {
        const targetRow = window.exchange_rates_table_data.data.find(obj => obj.symbol === targetCurrency.symbol);

        if (targetRow?.rates?.length) {
            const inverse = targetRow.rates.find(r => r.symbol === baseCurrency.symbol);
            if (inverse?.value !== undefined && inverse?.value !== null && String(inverse.value).trim() !== '') {
                return inverse.value;
            }
        }

        const n = Number(targetCurrency.value);
        if (!Number.isNaN(n) && n !== 0) {
            return (1 / n).toFixed(2);
        }

        return '-';
    }

    formatEurVolume(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return '-';
        }

        const n = Number(value);
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(n);
        return `${formatted} EUR`;
    }

    formatDotNumberString(value) {
        if (value === null || value === undefined) {
            return '-';
        }

        const raw = String(value).trim();
        if (raw === '' || raw === '-') {
            return '-';
        }

        const negative = raw.startsWith('-');
        const unsigned = negative ? raw.slice(1) : raw;

        const parts = unsigned.split('.', 2);
        const intPart = parts[0] ?? '0';
        const decPart = parts[1] ?? '';

        const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        if (decPart) {
            return `${negative ? '-' : ''}${intWithCommas}.${decPart}`;
        }

        return `${negative ? '-' : ''}${intWithCommas}`;
    }
}